import { ImageScaleView, ViewOpts } from '@play-co/timestep-core/ui';
import { Avatar } from 'src/game/components/shared/Avatar';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { user } from 'src/StateObserver';
import { WeeklyLeaderboardMemberData } from '../weeklyLeaderboard.utils';
import { LeaderboardsScore } from './LeaderboardsScore';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { Feature } from 'src/replicant/analytics';

type LeaderboardsMemberEntryOpts = ViewOpts & {
  weeklyPage?: boolean;
  onEntryClick?: (position: number) => Promise<void>;
};

export class LeaderboardsMemberEntry extends ButtonScaleView {
  private readonly bg: ImageScaleView;
  private readonly positionText: LangBitmapFontTextView;
  private readonly positionIcon: ImageScaleView;
  private readonly avatar: Avatar;
  private readonly name: LangBitmapFontTextView;
  private readonly level: LangBitmapFontTextView;
  private readonly levelLabel: LangBitmapFontTextView;
  private readonly score: LeaderboardsScore;
  private readonly weeklyPage: boolean;
  private data!: WeeklyLeaderboardMemberData;

  constructor(opts: LeaderboardsMemberEntryOpts) {
    super({
      width: 700,
      height: 92,
      ...opts,
      onClick: async () => opts.onEntryClick?.(this.data.position),
      tracking: {
        feature: Feature.WEEKLY_LEADERBOARD._,
        $subFeature: Feature.WEEKLY_LEADERBOARD.PLAYER_ENTRY,
      },
    });

    const { weeklyPage = false } = opts;

    this.weeklyPage = weeklyPage;

    this.bg = new ImageScaleView({
      superview: this,
      width: this.style.width,
      height: this.style.height + 4,
      image: 'assets/ui/match2/common/list_entry.png',
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 30, right: 30 },
        vertical: { top: 30, bottom: 30 },
      },
    });

    this.name = new LangBitmapFontTextView({
      superview: this,
      x: 155,
      y: -2,
      align: 'left',
      verticalAlign: 'center',
      size: 30,
      font: 'Body',
      width: weeklyPage ? 330 : 280,
      height: this.style.height,
      color: '#78263E',
    });

    this.positionText = new LangBitmapFontTextView({
      superview: this,
      x: 40,
      y: this.style.height * 0.5,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      font: 'Title',
      width: this.style.height,
      height: this.style.height,
    });

    this.positionIcon = new ImageScaleView({
      superview: this,
      x: 40,
      y: this.style.height * 0.5 - 2,
      centerOnOrigin: true,
      scaleMethod: 'contain',
      align: 'center',
      verticalAlign: 'center',
      width: 62,
      height: 62,
    });

    this.avatar = new Avatar({
      superview: this,
      width: 70,
      height: 70,
      x: 110,
      y: this.style.height * 0.5 - 3,
      centerOnOrigin: true,
    });

    this.score = new LeaderboardsScore({
      superview: this,
      x: weeklyPage ? 610 : 540,
      y: this.style.height * 0.5,
      scale: weeklyPage ? 1 : 0.9,
      centerOnOrigin: true,
    });

    if (!weeklyPage) {
      this.score.icon.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/leaderboards_medal_friends.png',
        y: this.score.style.height * 0.5 - 5,
        centerOnOrigin: true,
      });
    }

    this.levelLabel = new LangBitmapFontTextView({
      superview: this,
      x: 610,
      y: 12,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      font: 'Body',
      width: 60,
      height: 30,
      color: '#754111',
      visible: weeklyPage ? false : true,
      localeText: () => i18n('profile.level'),
    });

    this.level = new LangBitmapFontTextView({
      superview: this,
      x: 610,
      y: 42,
      align: 'center',
      verticalAlign: 'center',
      size: 28,
      font: 'Title',
      width: 60,
      height: 34,
      color: '#ffffff',
      visible: weeklyPage ? false : true,
      localeText: () => `999`,
    });
  }

  public setup(data: WeeklyLeaderboardMemberData) {
    const {
      id,
      score,
      name,
      photo,
      frame,
      level,
      position,
      leaguesAllTimeScore,
    } = data;
    const me = id === user().id;

    this.data = data;
    this.name.localeText = () => name;
    this.name.updateOpts({ y: 0 });

    if (position >= 1 && position <= 3) {
      this.positionIcon.updateOpts({
        image: `assets/ui/weeklyLeaderboard/popups/popupLeaderboards/leaderboards_medal_${position}.png`,
        visible: true,
      });
      this.positionText.updateOpts({ visible: false });
    } else {
      this.positionText.updateOpts({ visible: true });
      this.positionIcon.updateOpts({ visible: false });
      this.positionText.localeText = () => position.toString();
    }

    this.avatar.updateImage(photo);
    this.avatar.setFrame(frame);

    if (level !== undefined) {
      this.levelLabel.show();
      this.level.show();
      this.level.localeText = () => level.toString();
    } else {
      this.levelLabel.hide();
      this.level.hide();
    }

    if (this.weeklyPage) {
      // Weekly page shows actual weekly score
      this.score.show();
      this.score.label.localeText = () => String(score ?? 0);
    } else {
      // Friends or global show all time leagues score instead, if any
      const scoreToDisplay = leaguesAllTimeScore ?? 0;
      if (scoreToDisplay) {
        this.score.show();
        this.score.label.localeText = () => scoreToDisplay.toString();
      } else {
        this.score.hide();
      }
    }

    if (me) {
      this.bg.updateOpts({
        image:
          'assets/ui/weeklyLeaderboard/popups/popupLeaderboards/panel_list_green.png',
        offsetY: -2,
        height: this.style.height + 2,
      });
      this.name.updateOpts({
        font: 'Title',
        color: '#ffffff',
      });
      return;
    }

    this.bg.updateOpts({
      image: 'assets/ui/match2/common/list_entry.png',
      offsetY: 0,
      height: this.style.height + 4,
    });
    this.name.updateOpts({
      font: 'Body',
      color: '#78263E',
    });
  }
}
